<template>
	<div class="bg-white rounded-2xl border hover:shadow-2xl border-slate-200 px-4 py-2 my-4 w-full  sm:max-w-3xl">
		<div class="flex flex-col sm:flex-row gap-6 items-start">
			<img
				:src="audio.thumbnail_url"
				:alt="audio.title"
				class="rounded-full w-32 sm:w-40 aspect-square object-cover rotating-image mx-auto"
				:class="{ 'is-playing': isPlaying }"
			/>
			<div class="flex flex-col items-start flex-1 p-2">
				<div
					class="flex flex-col sm:flex-row gap-2 items-center justify-between w-full pb-3 border-b border-slate-200"
				>
					<div class="flex flex-row gap-2 items-center w-full justify-between sm:justify-start">
						<div
							class="text-gray-500 border border-gray-500 rounded-full text-xs px-3 py-1"
						>
							<p>{{ audio.episode_number }}</p>
						</div>
						<div
							class="flex flex-row gap-2 items-center font-medium text-gray-500 text-xs px-3 py-1"
						>
							<img src="~/assets/img/Calendar.svg" alt="Calendar" />
							<p>
								{{
									new Date(audio.created_at).toLocaleDateString("en-US", {
										month: "short",
										day: "2-digit",
										year: "numeric"
									})
								}}
							</p>
						</div>
					</div>
					<div class="flex flex-row gap-2 flex-wrap justify-end w-full mt-2">
						<div
							v-for="(cat, index) in audio.categories"
							:key="index"
							class="text-brand border border-brand rounded-full text-xs px-3 py-1"
						>
							<p>{{ cat }}</p>
						</div>
					</div>
				</div>
				<div class="flex flex-col w-full">
					<h1 class="text-sm sm:text-base font-bold text-brand uppercase mt-2">
						{{ audio.title }}
					</h1>
					<h2 class="text-slate-500 font-medium text-sm">
						{{ audio.subtitle }}
					</h2>

					<div class="w-full mt-2">
						<div @click="toggleDescription" class="relative cursor-pointer">
							<p
								class="text-slate-400 text-sm pr-6 text-justify"
								:class="{ 'line-clamp-2': !isDescriptionExpanded }"
							>
								{{ audio.description }}
							</p>
							<div class="absolute top-0 right-0 text-brand">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									class="h-5 w-5 transition-transform"
									:class="{ 'rotate-180': isDescriptionExpanded }"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M19 9l-7 7-7-7"
									/>
								</svg>
							</div>
						</div>
					</div>
				</div>
				<div
					class="flex flex-col sm:flex-row w-full gap-2 items-center mt-3 border-t border-slate-200"
				>
					<div
						class="flex flex-row gap-2 items-center mt-4 bg-slate-200 w-full px-4 py-2 rounded-lg"
					>
						<button @click="backToStart" class="mr-1">
							<img class="w-8" src="~/assets/img/Rewind.svg" alt="Back to Start" />
						</button>
						<button @click="togglePlay">
							<img class="w-16" :src="playPauseIcon" alt="Play/Pause" />
						</button>
						<div>
							<p class="font-medium text-gray-500 text-xs px-3 py-1">
								{{ currentTime }}
							</p>
						</div>
						<div class="flex flex-col w-full">
							<div
								class="w-full h-2 bg-slate-400 rounded-full cursor-pointer"
								@click="handleProgressClick"
								ref="progressBar"
							>
								<div
									:style="{ width: progress + '%' }"
									class="h-2 bg-brand rounded-full"
								></div>
							</div>
						</div>
						<div>
							<p class="font-medium text-gray-500 text-xs px-3 py-1">{{ duration }}</p>
						</div>
					</div>
					<div class="flex gap-2 mt-4">
						<button
							v-if="audio.meditationTimestamp"
							@click="skipToMeditation"
							class="text-nowrap self-start bg-brand text-xs text-white rounded-full px-4 py-1 font-medium hover:bg-opacity-80 transition-colors flex items-center gap-1"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								class="h-6 w-6"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path
									fill-rule="evenodd"
									d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
									clip-rule="evenodd"
								/>
							</svg>
							Skip to Meditation
						</button>
					</div>
					<!--<button @click="handleDownload">
              <img class="hidden w-6" :src="downloadIcon" alt="Download" />
            </button>
            <button @click="handleShare">
              <img class="hidden w-6" :src="shareIcon" alt="Share" />
            </button>
            <button @click="handleExpand">
              <img class="hidden w-6" :src="expandIcon" alt="Expand" />
            </button>-->
				</div>
			</div>
		</div>
		<div v-if="audioError" class="mt-4 p-2 bg-red-100 text-red-700 rounded">
			Error: {{ audioError }}
		</div>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
// Import the required images
import downloadIcon from "~/assets/img/Download.svg";
import shareIcon from "~/assets/img/Share.svg";
import expandIcon from "~/assets/img/Expand screen.svg";
import playIcon from "~/assets/img/play=true.svg";
import pauseIcon from "~/assets/img/play=false.svg";
import rewindIcon from "~/assets/img/Rewind.svg";

const props = defineProps({
	audio: {
		type: Object,
		required: true
	}
});

// Computed property to handle backward compatibility

const isPlaying = ref(false);
const progress = ref(0);
const currentTime = ref("00:00");
const duration = ref("00:00");
const lastPlaybackPosition = ref(0); // Store the last playback position
const playPauseIcon = computed(() => (isPlaying.value ? pauseIcon : playIcon));
const audioElement = ref(null);
const audioError = ref(null);
const progressBar = ref(null);
const isDescriptionExpanded = ref(false);

// Toggle description expansion
const toggleDescription = () => {
	isDescriptionExpanded.value = !isDescriptionExpanded.value;
};

// Format seconds to MM:SS format
const formatTime = (seconds) => {
	if (!seconds || isNaN(seconds)) return "00:00";
	const mins = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60);
	return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

// Convert timestamp string (MM:SS) to seconds
const timestampToSeconds = (timestamp) => {
	if (!timestamp || timestamp === "") return 0;

	const parts = timestamp.split(":");
	if (parts.length !== 2) return 0;

	const minutes = parseInt(parts[0], 10);
	const seconds = parseInt(parts[1], 10);

	if (isNaN(minutes) || isNaN(seconds)) return 0;

	return minutes * 60 + seconds;
};

// Skip to meditation timestamp
const skipToMeditation = () => {
	if (!audioElement.value || !props.audio.meditationTimestamp) return;

	const targetSeconds = timestampToSeconds(props.audio.meditationTimestamp);

	// Check if the audio is loaded and has a valid duration
	if (!audioElement.value.duration) {
		// If duration is not available yet, set up a one-time event listener
		const onceLoaded = () => {
			if (targetSeconds > 0 && targetSeconds < audioElement.value.duration) {
				audioElement.value.currentTime = targetSeconds;

				// Start playing if not already playing
				if (!isPlaying.value) {
					togglePlay();
				}
			}
			audioElement.value.removeEventListener("loadedmetadata", onceLoaded);
		};

		audioElement.value.addEventListener("loadedmetadata", onceLoaded);

		// Try to load the audio
		audioElement.value.load();
		return;
	}

	// If duration is available, skip to the timestamp
	if (targetSeconds > 0 && targetSeconds < audioElement.value.duration) {
		audioElement.value.currentTime = targetSeconds;

		// Start playing if not already playing
		if (!isPlaying.value) {
			togglePlay();
		}
	}
};

// Handle click on progress bar to seek
const handleProgressClick = (event) => {
	if (!audioElement.value || !progressBar.value) return;

	const rect = progressBar.value.getBoundingClientRect();
	const offsetX = event.clientX - rect.left;
	const clickPositionPercentage = offsetX / rect.width;

	// Set current time based on click position
	if (audioElement.value.duration) {
		audioElement.value.currentTime =
			clickPositionPercentage * audioElement.value.duration;
	}
};

// Initialize audio element
onMounted(() => {
	try {
		// Create audio element
		audioElement.value = new Audio();

		// Set up event listeners
		audioElement.value.addEventListener("loadedmetadata", () => {
			console.log("Audio metadata loaded, duration:", audioElement.value.duration);
			duration.value = formatTime(audioElement.value.duration);
		});

		audioElement.value.addEventListener("timeupdate", () => {
			const currentTimeSeconds = audioElement.value.currentTime;
			currentTime.value = formatTime(currentTimeSeconds);

			// Update progress bar
			if (audioElement.value.duration && !isNaN(audioElement.value.duration)) {
				progress.value = (currentTimeSeconds / audioElement.value.duration) * 100;
			}
		});

		audioElement.value.addEventListener("ended", () => {
			isPlaying.value = false;
			audioElement.value.currentTime = 0;
			lastPlaybackPosition.value = 0; // Reset the saved position
			progress.value = 0;
		});

		audioElement.value.addEventListener("error", (e) => {
			console.error("Audio error:", e);
			audioError.value = `Cannot play audio: ${
				e.target.error ? e.target.error.message : "Unknown error"
			}`;
		});

		// Set the source and load the audio
		audioElement.value.src = props.audio.file_url;
		audioElement.value.load();
		console.log("Audio source set to:", props.audioSrc);

		// Preload audio
		audioElement.value.preload = "auto";
	} catch (error) {
		console.error("Error setting up audio:", error);
		audioError.value = `Setup error: ${error.message}`;
	}
});

const togglePlay = () => {
	if (!audioElement.value) return;

	try {
		isPlaying.value = !isPlaying.value;

		if (isPlaying.value) {
			// When resuming, ensure we're at the last position
			if (lastPlaybackPosition.value > 0 && audioElement.value.currentTime === 0) {
				audioElement.value.currentTime = lastPlaybackPosition.value;
			}

			const playPromise = audioElement.value.play();

			if (playPromise !== undefined) {
				playPromise.catch((error) => {
					console.error("Play error:", error);
					isPlaying.value = false;
					audioError.value = `Play error: ${error.message}`;
				});
			}
		} else {
			// When pausing, store the current position
			lastPlaybackPosition.value = audioElement.value.currentTime;
			audioElement.value.pause();
		}
	} catch (error) {
		console.error("Toggle play error:", error);
		audioError.value = `Play/pause error: ${error.message}`;
	}
};

// Watch for changes to the audioSrc prop and reload if needed
watch(
	() => props.audioSrc,
	(newSrc, oldSrc) => {
		if (newSrc !== oldSrc && audioElement.value) {
			audioElement.value.src = newSrc;
			audioElement.value.load();
			lastPlaybackPosition.value = 0; // Reset the saved position
			progress.value = 0;
			currentTime.value = "00:00";
			isPlaying.value = false;
		}
	},
	{ immediate: false }
);

const handleDownload = () => {
	// Create a temporary link to download the file
	const link = document.createElement("a");
	link.href = props.audioSrc;
	link.download = `${props.title.replace(/\s+/g, "-").toLowerCase()}.mp4`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

const handleShare = () => {
	// Add share logic here
};

const handleExpand = () => {
	// Add expand logic here
};

// Back to Start function
const backToStart = () => {
	if (!audioElement.value) return;

	audioElement.value.currentTime = 0;
	lastPlaybackPosition.value = 0;

	// Start playing if not already playing
	if (!isPlaying.value) {
		togglePlay();
	}
};
</script>

<style scoped>
@keyframes spin-slow {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}

.rotating-image {
	animation: spin-slow 20s linear infinite;
	animation-play-state: paused;
}

.is-playing {
	animation-play-state: running;
}
</style>
